import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, Check, ChevronDown, ChevronRight, Mail, MessageCircle, Pencil, Phone, Printer, X } from "lucide-react";
import { api } from "./api";
import { Recherche, usePagePaysage } from "./ui";

/* Spectacles › Bénévoles : un tableau de service, comme un planning du personnel (Jen,
   01.11.2026 : « au final c'est un menu pour faire de la gestion de personnel et voir qui
   manque rapidement — l'écran est busy, difficile de voir qui fait quoi et qui il manque »).
   · En haut, la synthèse : « Il manque N bénévoles sur M », puis une tuile par date (clic :
     seulement cette date) ; Écrire, Groupes WhatsApp et Imprimer, discrets, à droite ;
   · « Par poste » : les postes en lignes × les représentations en colonnes ; chaque case dit
     « pris/places » en couleur (vert complet, orange il en manque, rose personne) avec les
     prénoms. Un clic ouvre le panneau de la case : nom, téléphone, e-mail, inscrit par qui,
     remarque, et Appeler, WhatsApp, E-mail, Modifier, Retirer. Une seule date : la fiche du
     jour (noms et téléphones, places à trouver) ;
   · « Par personne » : une ligne par bénévole, ses créneaux et son contact, recherche ;
   · Modifier (école, sans limite de 48 h) : la date, le poste (complet : seulement en le
     disant), les coordonnées, la remarque, « Prévenir par e-mail » coché d'office ;
   · Imprimer : le tableau de service, la liste par date (une page par date, téléphones et
     lignes vides à remplir) ou la liste des bénévoles — A4 paysage. */

export const EMOJI = { tombola: "🎟️", patisserie: "🧁", entrees: "🎫", surveillance: "🧒", maquillage: "💄", parking: "🚗" };
export const COURT = { tombola: "Tombola", patisserie: "Pâtisserie", entrees: "Entrées", surveillance: "Surveillance", maquillage: "Maquillage", parking: "Parking" };

const TEINTES = {
  complet: { fond: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D", point: "#22C55E", mot: "Complet" },
  partiel: { fond: "#FFFBEB", bord: "#FDE68A", texte: "#B45309", point: "#F59E0B", mot: "Il en manque" },
  vide: { fond: "#FFF5F5", bord: "#FECACA", texte: "#BE123C", point: "#F43F5E", mot: "Personne" },
};
const majuscule = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : "");
const sansAnnee = (t) => String(t || "").replace(/ 2027/, "");
// « samedi 12 juin 2027 à 18h » → « Sam 12 juin » et « 18h ».
const jourCourt = (x) => { const [jour = "", n = "", mois = ""] = String(x.quand || "").split(" "); return `${majuscule(jour.slice(0, 3))} ${n} ${mois}`; };
const heure = (x) => String(x.quand || "").split(" à ")[1] || "";
const dateCourte = (x) => `${jourCourt(x)} · ${heure(x)}`;
const nomCourt = (b) => `${b.prenom} ${b.nom ? `${b.nom.trim().charAt(0).toUpperCase()}.` : ""}`.trim();
const sansAccents = (t) => String(t || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
const cleDe = (b) => sansAccents(`${b.prenom} ${b.nom}|${b.email}`).trim();
const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;
const etatDe = (pris, places) => (pris >= places ? "complet" : pris > 0 ? "partiel" : "vide");
// « Anne Golay <anne@…> » : la personne qui a inscrit le bénévole dans sa commande.
const parQui = (b) => { const m = /^(.*?)\s*<([^>]*)>\s*$/.exec(b.commande_par || ""); return m ? { nom: m[1].trim(), email: m[2].trim().toLowerCase() } : null; };
// « 079 123 45 67 » → « 41791234567 » pour wa.me.
const numeroWhatsApp = (tel) => {
  let d = String(tel || "").replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = `41${d.slice(1)}`;
  return d.length >= 9 ? d : "";
};
const ecrire = (groupe) => window.dispatchEvent(new CustomEvent("console-aller", { detail: { onglet: "envois", id: `benevoles::${groupe}` } }));
const inscritLe = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("fr-CH", { day: "numeric", month: "long" }) : ""; } catch { return ""; } };
const lireVue = () => { try { return localStorage.getItem("benevoles-vue") === "personne" ? "personne" : "poste"; } catch { return "poste"; } };
const garderVue = (v) => { try { localStorage.setItem("benevoles-vue", v); } catch { /* navigation privée */ } };

/* Ce que devient son billet offert quand on le retire (comme le serveur) : annulé s'il est à
   part, sinon un billet de moins dans sa commande, même à côté de billets payés (Jen,
   01.11.2026) — sauf si un enfant de 0-2 ans y perdait son adulte. */
const texteRetirer = (b, commandes = []) => {
  if (!b.billet_id || b.sans_billet) return "Sa place de bénévole se libère.";
  const c = commandes.find((x) => x.id === b.billet_id);
  if (!b.commande_ref) return "Sa place de bénévole se libère et son billet offert est annulé.";
  if (c && Number(c.bebe || 0) > Number(c.plein || 0) - 1) return "Sa place de bénévole se libère. Son billet offert reste dans sa commande (un enfant de 0-2 ans y garde son adulte) : vois avec la personne.";
  return "Sa place de bénévole se libère et son billet offert est retiré de sa commande (les billets payés restent).";
};
// Le billet offert d'un créneau : où il est, en une ligne.
const texteBillet = (c, reps) => {
  if (c.sans_billet) return "Sans billet";
  const r = reps.find((x) => x.id === c.billet_representation_id);
  if (!c.billet_id) return c.statut === "inscrit" ? "Billet offert à la confirmation" : "";
  const p = parQui(c);
  return `Billet offert : ${r ? dateCourte(r) : "—"}${c.commande_ref && p ? ` (dans les billets de ${p.nom})` : ""}`;
};

export default function BenevolesSpectacles({ d, demander, majDonnees, cible }) {
  usePagePaysage();
  const [vue, setVue] = useState(lireVue);
  const [rep, setRep] = useState("");
  const [q, setQ] = useState("");
  // `cible` : un créneau (e-mail « Nouveau bénévole » à l'école) — sa fiche s'ouvre ; s'il
  // n'existe plus, le panneau se referme tout seul (plus bas).
  const [panneau, setPanneau] = useState(() => (cible ? { type: "personne", bid: cible } : null));
  const [bulle, setBulle] = useState(null);
  const [imprime, setImprime] = useState("");

  const reps = useMemo(() => d.representations.filter((x) => x.ouverte || x.benevoles.length), [d]);
  const roles = reps[0]?.roles || [];
  const creneaux = useMemo(() => reps.flatMap((x) => x.benevoles.map((b) => ({ ...b, rep: x }))), [reps]);
  const personnes = useMemo(() => {
    const m = new Map();
    creneaux.forEach((c) => {
      const k = cleDe(c);
      if (!m.has(k)) m.set(k, { cle: k, prenom: c.prenom, nom: c.nom, email: c.email, telephone: c.telephone, commande_par: c.commande_par, creneaux: [] });
      m.get(k).creneaux.push(c);
    });
    return [...m.values()].sort((a, b) => `${a.prenom} ${a.nom}`.localeCompare(`${b.prenom} ${b.nom}`, "fr"));
  }, [creneaux]);
  const personneDe = (c) => personnes.find((p) => p.cle === cleDe(c)) || { creneaux: [c], ...c };
  const visibles = reps.filter((x) => !rep || x.id === rep);
  const choisie = reps.find((x) => x.id === rep);

  // Les chiffres : les représentations ouvertes (le samedi 19 fermé ne compte pas).
  const ouvertes = reps.filter((x) => x.ouverte);
  const places = ouvertes.reduce((n, x) => n + x.roles.reduce((m, r) => m + r.places, 0), 0);
  const manque = ouvertes.reduce((n, x) => n + x.roles.reduce((m, r) => m + r.restantes, 0), 0);
  const aConfirmer = creneaux.filter((c) => c.statut === "inscrit");
  const whatsapps = [...new Map(reps.map((x) => [x.whatsapp, x.titre])).entries()].filter(([lien]) => lien);

  const afficher = (texte, ton = "ok") => setBulle({ texte, ton, n: Date.now() });
  useEffect(() => {
    if (!bulle) return undefined;
    const t = setTimeout(() => setBulle(null), bulle.ton === "ok" ? 7000 : 12000);
    return () => clearTimeout(t);
  }, [bulle]);
  useEffect(() => {
    const fin = () => setImprime("");
    window.addEventListener("afterprint", fin);
    return () => window.removeEventListener("afterprint", fin);
  }, []);

  const faire = async (chemin, options, succes) => {
    try {
      const r = await api(chemin, options);
      if (r && r.representations) majDonnees(r);
      afficher(succes || r?.message || "C'est fait.");
      return r;
    } catch (e) { afficher(e.message, "erreur"); return null; }
  };
  // « Prévenir … par e-mail », coché d'office comme dans « Modifier » (Jen, 01.11.2026).
  const retirer = async (c) => {
    const r = await demander({ titre: `Retirer ${c.prenom} ${c.nom} ?`, texte: `${COURT[c.role]} · ${c.rep.titre}, ${sansAnnee(c.rep.quand)}. ${texteRetirer(c, d.commandes)}`, valider: "Retirer",
      option: c.email ? { libelle: `Prévenir ${c.prenom} par e-mail`, cochee: true } : null });
    if (!r) return;
    const prevenir = !!(r && r.option);
    const fait = await faire(`/billetterie/benevoles/${c.id}/annuler`, { method: "POST", body: JSON.stringify({ prevenir }) },
      `${c.prenom} ${c.nom} est retiré·e de ${COURT[c.role]} (${dateCourte(c.rep)}). ${texteRetirer(c, d.commandes)}`);
    if (fait) afficher(`${c.prenom} ${c.nom} est retiré·e de ${COURT[c.role]} (${dateCourte(c.rep)}). ${texteRetirer(c, d.commandes)} ${fait.e_mail ? (fait.e_mail.includes("@") ? `E-mail envoyé à ${fait.e_mail}.` : `E-mail : ${fait.e_mail}.`) : "Aucun e-mail envoyé."}`);
  };
  const confirmer = (c) => faire(`/billetterie/benevoles/${c.id}/confirmer`, { method: "POST" },
    `${c.prenom} confirmé·e : e-mail avec le lien du groupe WhatsApp${c.commande_ref || c.sans_billet ? "" : " et son billet offert"}.`);
  const actions = { retirer, confirmer, modifier: (c) => setPanneau((p) => ({ type: "modifier", bid: c.id, retour: p })) };

  const changerVue = (v) => { setVue(v); garderVue(v); };
  const imprimer = (mode) => { setImprime(mode); setTimeout(() => window.print(), 120); };
  const modeImpression = imprime || (vue === "personne" ? "personnes" : rep ? "dates" : "tableau");

  // Le panneau suit les données (après Modifier ou Retirer, il montre l'état à jour).
  let contenuPanneau = null;
  if (panneau?.type === "case") {
    const x = reps.find((r) => r.id === panneau.rep);
    const role = x?.roles.find((r) => r.role === panneau.role);
    if (x && role) {
      contenuPanneau = <PanneauCase x={x} role={role} gens={creneaux.filter((c) => c.rep.id === x.id && c.role === role.role)}
        focus={panneau.focus} reps={reps} personneDe={personneDe} actions={actions} onFermer={() => setPanneau(null)} />;
    }
  } else if (panneau?.type === "personne") {
    const c = creneaux.find((x) => x.id === panneau.bid);
    if (c) contenuPanneau = <PanneauPersonne p={personneDe(c)} reps={reps} actions={actions} onFermer={() => setPanneau(null)} />;
  } else if (panneau?.type === "modifier") {
    const c = creneaux.find((x) => x.id === panneau.bid);
    if (c) {
      contenuPanneau = <FormulaireModifier key={c.id} c={c} d={d} personne={personneDe(c)} majDonnees={majDonnees}
        onRetour={() => setPanneau(panneau.retour)} onFermer={() => setPanneau(null)}
        onEnregistre={(message) => { afficher(message); setPanneau(panneau.retour || null); }} />;
    }
  }
  // Plus personne à montrer (son dernier créneau retiré) : le panneau se referme pour de bon.
  const panneauVide = Boolean(panneau) && !contenuPanneau;
  useEffect(() => { if (panneauVide) setPanneau(null); }, [panneauVide]);

  return (
    <div className="space-y-4" data-testid="benevoles-service">
      <div className="space-y-4 print:hidden">
        <Synthese reps={reps} rep={rep} setRep={setRep} manque={manque} places={places} nbPersonnes={personnes.length}
          nbCreneaux={creneaux.length} whatsapps={whatsapps} choisie={choisie} imprimer={imprimer} />
        {aConfirmer.length > 0 && (
          <div className="rounded-2xl px-4 py-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }} data-testid="benevoles-a-confirmer">
            <AlertTriangle size={15} />
            <span className="flex-1 min-w-[12rem]">{aConfirmer.length > 1 ? `${aConfirmer.length} anciens créneaux ne sont pas encore confirmés` : "Un ancien créneau n'est pas encore confirmé"} (inscrits avant la confirmation d'office) :</span>
            {aConfirmer.map((c) => (
              <button key={c.id} type="button" className="underline underline-offset-2 font-medium" onClick={() => setPanneau({ type: "personne", bid: c.id })}>
                {c.prenom} {c.nom} · {COURT[c.role]}, {dateCourte(c.rep)}</button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <Segments valeur={vue} onChange={changerVue} options={[["poste", "Par poste"], ["personne", "Par personne"]]} />
          {vue === "poste" ? <Legende /> : (
            <span className="ml-auto"><Recherche valeur={q} onChange={setQ} placeholder="Nom, e-mail ou téléphone" testid="benevoles-recherche" largeur={280} /></span>
          )}
        </div>
        {vue === "poste" && (choisie
          ? <FicheDuJour x={choisie} creneaux={creneaux} onOuvrir={(role, focus) => setPanneau({ type: "case", rep: choisie.id, role, focus })} />
          : <TableauService reps={visibles} roles={roles} onOuvrir={(x, role, focus) => setPanneau({ type: "case", rep: x.id, role, focus })} />)}
        {vue === "personne" && <ListePersonnes personnes={personnes} rep={rep} q={q} onOuvrir={(c) => setPanneau({ type: "personne", bid: c.id })} />}
        <p className="text-xs text-[var(--muted)] leading-relaxed">
          Les bénévoles sont confirmés d'office : dès l'inscription, ils reçoivent le lien du groupe WhatsApp, leur billet offert et un lien
          pour changer ou annuler (jusqu'à 48 h avant). Ici, l'école peut à tout moment changer une date ou un poste (« Modifier »), corriger
          un contact ou retirer quelqu'un.
        </p>
      </div>
      <div className="hidden print:block fiche-impression" data-testid="benevoles-impression">
        <Impression mode={modeImpression} reps={modeImpression === "tableau" ? reps : visibles} roles={roles} creneaux={creneaux}
          personnes={personnes} rep={rep} manque={manque} places={places} />
      </div>
      {contenuPanneau && <Tiroir onFermer={() => setPanneau(null)}>{contenuPanneau}</Tiroir>}
      {bulle && <Bulle bulle={bulle} onFermer={() => setBulle(null)} />}
    </div>
  );
}

/* ── La synthèse ───────────────────────────────────────────────────────────────────── */

function Synthese({ reps, rep, setRep, manque, places, nbPersonnes, nbCreneaux, whatsapps, choisie, imprimer }) {
  const pris = places - manque;
  return (
    <section className="bg-white rounded-2xl border p-5 space-y-4" style={{ borderColor: "var(--border)" }} data-testid="benevoles-synthese">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="flex-1 min-w-[16rem]">
          <h2 className="font-display text-2xl tracking-tight leading-tight" data-testid="benevoles-titre">
            {manque > 0
              ? <>Il manque <span style={{ color: "#B45309" }} data-testid="benevoles-manque">{manque}</span> bénévole{manque > 1 ? "s" : ""} <span className="text-[var(--muted)]">sur {places}</span></>
              : <>Tous les postes sont pris <span style={{ color: "#15803D" }}>✓</span></>}
          </h2>
          <p className="text-sm text-[var(--muted)] mt-1">
            {pluriel(pris, "poste pris", "postes pris")} par {pluriel(nbPersonnes, "personne", "personnes")}
            {nbCreneaux !== pris ? ` · ${pluriel(nbCreneaux, "créneau", "créneaux")} en tout` : ""}
          </p>
          <div className="h-1.5 rounded-full mt-3 overflow-hidden max-w-md" style={{ background: "#F1F5F9" }}>
            <div className="h-full rounded-full" style={{ width: `${places ? Math.round((pris / places) * 100) : 0}%`, background: manque ? "#F59E0B" : "#22C55E" }} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="benevoles-actions">
          <button type="button" className="btn-ghost text-xs" onClick={() => ecrire(rep || "tous")} data-testid="benevoles-ecrire">
            <Mail size={13} /> {choisie ? `Écrire aux bénévoles du ${jourCourt(choisie).toLowerCase()}` : "Écrire à tous"}</button>
          {whatsapps.length === 1 ? (
            <a className="btn-ghost text-xs" href={whatsapps[0][0]} target="_blank" rel="noopener noreferrer" data-testid="benevoles-whatsapp"><MessageCircle size={13} /> Groupe WhatsApp</a>
          ) : whatsapps.length > 1 && (
            <Menu libelle="Groupes WhatsApp" Icon={MessageCircle} testid="benevoles-whatsapp" items={whatsapps.map(([lien, titre]) => ({ cle: lien, titre, href: lien }))} />
          )}
          <Menu libelle="Imprimer" Icon={Printer} testid="benevoles-imprimer" alignDroite items={[
            { cle: "tableau", titre: "Le tableau de service", detail: "Toutes les dates sur une page", onClick: () => imprimer("tableau") },
            { cle: "dates", titre: choisie ? `La liste du ${jourCourt(choisie).toLowerCase()}` : "La liste par date", detail: "Une page par date : postes, noms, téléphones, places à trouver", onClick: () => imprimer("dates") },
            { cle: "personnes", titre: "La liste des bénévoles", detail: "Une ligne par personne, avec ses créneaux", onClick: () => imprimer("personnes") },
          ]} />
        </div>
      </div>
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }} data-testid="benevoles-dates">
        <TuileDate actif={!rep} onClick={() => setRep("")} titre="Toutes les dates" testid="benevoles-date-toutes"
          ligne={pluriel(reps.filter((x) => x.ouverte).length, "représentation", "représentations")} />
        {reps.map((x) => {
          const p = x.roles.reduce((n, r) => n + r.places, 0);
          const m = x.roles.reduce((n, r) => n + r.restantes, 0);
          return (
            <TuileDate key={x.id} actif={rep === x.id} onClick={() => setRep(rep === x.id ? "" : x.id)} surtitre={x.titre} titre={dateCourte(x)}
              pourcent={p ? (p - m) / p : 0} ferme={!x.ouverte} testid={`benevoles-date-${x.id}`}
              ligne={!x.ouverte ? "Fermée" : m ? `il en manque ${m}` : "complet ✓"} ton={!x.ouverte ? "var(--muted)" : m ? "#B45309" : "#15803D"} />
          );
        })}
      </div>
    </section>
  );
}

function TuileDate({ actif, onClick, surtitre, titre, ligne, ton, pourcent, ferme, testid }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={actif} data-testid={testid}
      className="rounded-xl border px-3 py-2.5 text-left transition-colors hover:bg-[var(--primary-tint)] min-w-0"
      style={{ borderColor: actif ? "var(--primary)" : "var(--border)", boxShadow: actif ? "0 0 0 1px var(--primary)" : "none", background: actif ? "var(--primary-tint)" : "#fff", opacity: ferme ? 0.6 : 1 }}>
      {surtitre && <span className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--muted)] truncate">{surtitre}</span>}
      <span className="block text-[13px] font-semibold truncate">{titre}</span>
      {pourcent !== undefined && (
        <span className="block h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: "#F1F5F9" }}>
          <span className="block h-full rounded-full" style={{ width: `${Math.round(pourcent * 100)}%`, background: pourcent >= 1 ? "#22C55E" : "#F59E0B" }} />
        </span>
      )}
      <span className="block text-[11px] mt-1 font-medium" style={{ color: ton || "var(--muted)" }}>{ligne}</span>
    </button>
  );
}

const Segments = ({ valeur, onChange, options }) => (
  <div className="inline-flex p-1 rounded-full" style={{ background: "#EEF1F4" }} role="tablist" data-testid="benevoles-vues">
    {options.map(([v, label]) => (
      <button key={v} type="button" role="tab" aria-selected={valeur === v} onClick={() => onChange(v)} data-testid={`benevoles-vue-${v}`}
        className="px-4 py-1.5 rounded-full text-[13px] transition-all"
        style={valeur === v ? { background: "#fff", color: "var(--text)", fontWeight: 600, boxShadow: "0 1px 3px rgba(15,27,45,0.12)" } : { color: "var(--muted)" }}>
        {label}
      </button>
    ))}
  </div>
);

const Legende = () => (
  <span className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--muted)]" data-testid="benevoles-legende">
    {Object.values(TEINTES).map((t) => (
      <span key={t.mot} className="inline-flex items-center gap-1.5">
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.point }} />{t.mot}
      </span>
    ))}
  </span>
);

function Menu({ libelle, Icon, items, testid, alignDroite }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    const echap = (e) => { if (e.key === "Escape") setOuvert(false); };
    document.addEventListener("mousedown", fermer);
    document.addEventListener("keydown", echap);
    return () => { document.removeEventListener("mousedown", fermer); document.removeEventListener("keydown", echap); };
  }, [ouvert]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="btn-ghost text-xs" onClick={() => setOuvert(!ouvert)} aria-expanded={ouvert} data-testid={testid}>
        <Icon size={13} /> {libelle} <ChevronDown size={12} />
      </button>
      {ouvert && (
        <div className={`absolute z-30 mt-1.5 w-72 max-w-[85vw] bg-white rounded-xl border p-1.5 ${alignDroite ? "right-0" : "left-0"}`}
          style={{ borderColor: "var(--border)", boxShadow: "0 12px 32px -8px rgba(15,27,45,0.22)" }} role="menu">
          {items.map((it) => {
            const Racine = it.href ? "a" : "button";
            return (
              <Racine key={it.cle} role="menuitem" {...(it.href ? { href: it.href, target: "_blank", rel: "noopener noreferrer" } : { type: "button" })}
                onClick={() => { setOuvert(false); if (it.onClick) it.onClick(); }} data-testid={`${testid}-${it.cle}`}
                className="block w-full text-left rounded-lg px-3 py-2 hover:bg-[var(--primary-tint)]">
                <span className="block text-sm font-medium">{it.titre}</span>
                {it.detail && <span className="block text-xs text-[var(--muted)]">{it.detail}</span>}
              </Racine>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Par poste : le tableau de service ─────────────────────────────────────────────── */

// Les places d'une case en pastilles : pleines pour les prises, vides pour celles à trouver.
const Pastilles = ({ pris, places, couleur }) => (
  <span className="inline-flex items-center gap-[3px]" aria-hidden>
    {Array.from({ length: places }, (_, i) => (
      <span key={i} className="w-[7px] h-[7px] rounded-full" style={i < pris ? { background: couleur } : { border: `1.5px solid ${couleur}`, opacity: 0.55 }} />
    ))}
    {pris > places && <span className="text-[10px] font-semibold ml-0.5" style={{ color: couleur }}>+{pris - places}</span>}
  </span>
);

const comptes = (role, gens) => {
  const pris = Math.max(gens.length, role.places - role.restantes);
  return { pris, manque: Math.max(0, role.places - pris), etat: etatDe(pris, role.places) };
};

// Les représentations qui se suivent pour le même spectacle : un en-tête au-dessus d'elles.
const parSpectacle = (reps) => reps.reduce((acc, x) => {
  const dernier = acc[acc.length - 1];
  if (dernier && dernier.titre === x.titre) dernier.n += 1; else acc.push({ titre: x.titre, n: 1 });
  return acc;
}, []);

function TableauService({ reps, roles, onOuvrir, impression }) {
  const corps = (
    <table className="w-full table-fixed" style={{ borderCollapse: "separate", borderSpacing: impression ? "3px" : "6px", minWidth: impression ? 0 : 660 }} data-testid={impression ? undefined : "benevoles-tableau"}>
      <colgroup><col style={{ width: impression ? "15%" : 148 }} />{reps.map((x) => <col key={x.id} />)}</colgroup>
      <thead>
        <tr>
          <th />
          {parSpectacle(reps).map((g) => (
            <th key={g.titre} colSpan={g.n} className="text-left px-1 pb-1">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)] border-b pb-1.5" style={{ borderColor: "var(--border)" }}>{g.titre}</span>
            </th>
          ))}
        </tr>
        <tr>
          <th className="text-left align-bottom px-1 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)]">Poste</th>
          {reps.map((x) => {
            const p = x.roles.reduce((n, r) => n + r.places, 0);
            const m = x.roles.reduce((n, r) => n + r.restantes, 0);
            return (
              <th key={x.id} className="text-left align-bottom px-1 pb-1 font-normal" data-testid={impression ? undefined : `benevoles-colonne-${x.id}`}>
                <span className="block text-[13px] font-semibold leading-tight">{jourCourt(x)}</span>
                <span className="block text-[12px] text-[var(--muted)] leading-tight">{heure(x)} · {p - m}/{p}{!x.ouverte ? " · fermée" : ""}</span>
                <span className="block text-[11px] font-semibold mt-0.5" style={{ color: m ? "#B45309" : "#15803D" }}>{m ? `manque ${m}` : "complet ✓"}</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {roles.map((role) => (
          <tr key={role.role}>
            <th scope="row" className="text-left align-top px-1 py-1.5 font-normal" title={role.description}>
              <span className="flex items-center gap-2">
                <span className="text-lg leading-none" aria-hidden>{EMOJI[role.role]}</span>
                <span className="text-[13px] font-semibold leading-tight">{COURT[role.role]}</span>
              </span>
            </th>
            {reps.map((x) => {
              const r = x.roles.find((y) => y.role === role.role);
              const gens = x.benevoles.filter((b) => b.role === role.role);
              return <td key={x.id} className="align-top p-0 h-px"><CaseService x={x} role={r} gens={gens} onOuvrir={onOuvrir} impression={impression} /></td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
  if (impression) return corps;
  return <div className="bg-white rounded-2xl border overflow-x-auto px-2 py-2" style={{ borderColor: "var(--border)" }}>{corps}</div>;
}

function CaseService({ x, role, gens, onOuvrir, impression }) {
  const { pris, manque, etat } = comptes(role, gens);
  const t = TEINTES[etat];
  const Racine = impression ? "div" : "button";
  return (
    <Racine {...(impression ? {} : { type: "button", onClick: (e) => onOuvrir(x, role.role, e.target.closest("[data-bid]")?.getAttribute("data-bid") || "") })}
      className={`w-full h-full min-h-[64px] text-left rounded-xl border px-2.5 py-2 ${impression ? "" : "transition-shadow hover:shadow-[0_4px_14px_-4px_rgba(15,27,45,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]"}`}
      style={{ background: t.fond, borderColor: t.bord, borderStyle: etat === "vide" ? "dashed" : "solid", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}
      title={impression ? undefined : `${COURT[role.role]} · ${dateCourte(x)} : ${pris}/${role.places}`}
      data-testid={impression ? undefined : `case-${x.id}-${role.role}`} data-etat={etat}>
      <span className="flex items-center justify-between gap-1.5">
        <span className="text-[13px] font-semibold tabular-nums" style={{ color: t.texte }}>{pris}/{role.places}</span>
        <Pastilles pris={pris} places={role.places} couleur={t.point} />
      </span>
      {gens.length > 0 && (
        <span className="block mt-1">
          {gens.map((b) => (
            <span key={b.id} data-bid={b.id} className={`flex items-center gap-1 text-[12px] leading-[1.35] min-w-0 ${impression ? "" : "hover:underline underline-offset-2"}`}>
              {b.statut === "inscrit" && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#F59E0B" }} title="Pas encore confirmé" />}
              <span className="truncate">{impression ? `${b.prenom} ${b.nom}` : nomCourt(b)}</span>
            </span>
          ))}
        </span>
      )}
      {manque > 0 && <span className="block text-[11px] mt-1" style={{ color: t.texte }}>{gens.length ? `+ ${manque} à trouver` : `${manque} à trouver`}</span>}
    </Racine>
  );
}

/* Une seule date : la fiche du jour — chaque poste avec ses bénévoles (nom et téléphone) et
   les places encore à trouver, en pointillés. */
function FicheDuJour({ x, creneaux, onOuvrir }) {
  const ici = creneaux.filter((c) => c.rep.id === x.id);
  return (
    <div className="bg-white rounded-2xl border divide-y" style={{ borderColor: "var(--border)" }} data-testid={`benevoles-jour-${x.id}`}>
      <div className="px-5 py-3 flex flex-wrap items-baseline gap-x-3">
        <strong className="text-base">{x.titre}</strong>
        <span>{majuscule(x.quand)}</span>
        <span className="text-[var(--muted)] text-sm">portes {x.portes}</span>
      </div>
      {x.roles.map((role) => {
        const gens = ici.filter((c) => c.role === role.role);
        const { pris, manque, etat } = comptes(role, gens);
        const t = TEINTES[etat];
        return (
          <div key={role.role} className="px-5 py-3 flex flex-wrap md:flex-nowrap gap-x-4 gap-y-2" style={{ borderColor: "var(--border-soft)" }} data-testid={`jour-${x.id}-${role.role}`}>
            <button type="button" onClick={() => onOuvrir(role.role, "")} className="w-48 shrink-0 text-left">
              <span className="flex items-center gap-2"><span className="text-lg" aria-hidden>{EMOJI[role.role]}</span><span className="font-semibold text-sm">{role.libelle}</span></span>
              <span className="flex items-center gap-2 mt-1 pl-7"><span className="text-xs font-semibold" style={{ color: t.texte }}>{pris}/{role.places}</span><Pastilles pris={pris} places={role.places} couleur={t.point} /></span>
            </button>
            <div className="flex flex-wrap gap-2 flex-1 min-w-0">
              {gens.map((c) => (
                <button key={c.id} type="button" onClick={() => onOuvrir(role.role, c.id)} className="rounded-xl border px-3 py-1.5 text-left bg-white hover:shadow-sm" style={{ borderColor: "var(--border)" }} data-testid="jour-personne">
                  <span className="block text-[13px] font-semibold">{c.statut === "inscrit" && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" style={{ background: "#F59E0B" }} />}{c.prenom} {c.nom}</span>
                  <span className="block text-xs text-[var(--muted)]">{c.telephone || c.email}</span>
                </button>
              ))}
              {Array.from({ length: manque }, (_, i) => (
                <button key={`vide-${i}`} type="button" onClick={() => onOuvrir(role.role, "")} className="rounded-xl border border-dashed px-3 py-1.5 text-xs self-stretch flex items-center" style={{ borderColor: TEINTES.vide.bord, color: TEINTES.vide.texte, background: TEINTES.vide.fond }}>à trouver</button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Par personne ──────────────────────────────────────────────────────────────────── */

function ListePersonnes({ personnes, rep, q, onOuvrir }) {
  const mot = sansAccents(q.trim());
  const chiffres = q.replace(/\D/g, "");
  const lignes = personnes
    .map((p) => ({ ...p, creneaux: p.creneaux.filter((c) => !rep || c.rep.id === rep) }))
    .filter((p) => p.creneaux.length)
    .filter((p) => !mot || sansAccents(`${p.prenom} ${p.nom} ${p.email} ${parQui(p)?.nom || ""}`).includes(mot)
      || (chiffres.length >= 3 && String(p.telephone || "").replace(/\D/g, "").includes(chiffres)));
  return (
    <div className="bg-white rounded-2xl border overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="benevoles-personnes">
      <table className="w-full text-sm" style={{ minWidth: 640 }}>
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
            <th className="px-4 py-2.5 font-semibold">Bénévole · {lignes.length}</th>
            <th className="px-4 py-2.5 font-semibold">Créneaux</th>
            <th className="px-4 py-2.5 font-semibold">Contact</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {lignes.map((p) => {
            const par = parQui(p);
            const wa = numeroWhatsApp(p.telephone);
            return (
              <tr key={p.cle} className="border-b last:border-b-0 align-top hover:bg-[#FAFBFC]" style={{ borderColor: "var(--border-soft)" }} data-testid="benevoles-personne">
                <td className="px-4 py-3">
                  <button type="button" className="font-semibold text-left hover:underline underline-offset-2" onClick={() => onOuvrir(p.creneaux[0])}>{p.prenom} {p.nom}</button>
                  {par && <div className="text-xs text-[var(--muted)]">inscrit·e par {par.nom}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {p.creneaux.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap" style={{ borderColor: "var(--border)" }}>
                        <span aria-hidden>{EMOJI[c.role]}</span>{COURT[c.role]}<span className="text-[var(--muted)]">· {dateCourte(c.rep)}</span>
                        {c.statut === "inscrit" && <span className="font-semibold" style={{ color: "#B45309" }}>· à confirmer</span>}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  {p.telephone && <a className="block text-[13px] font-medium whitespace-nowrap hover:underline" href={`tel:${p.telephone}`}>{p.telephone}</a>}
                  {p.email && <a className="block text-[var(--muted)] hover:underline break-all" href={`mailto:${p.email}`}>{p.email}</a>}
                </td>
                <td className="px-4 py-3">
                  <span className="flex justify-end gap-0.5">
                    {p.telephone && <a className="p-1.5 rounded-full hover:bg-[var(--primary-tint)]" href={`tel:${p.telephone}`} title={`Appeler ${p.telephone}`}><Phone size={15} /></a>}
                    {wa && <a className="p-1.5 rounded-full hover:bg-[var(--primary-tint)]" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" title="WhatsApp"><MessageCircle size={15} /></a>}
                    {p.email && <a className="p-1.5 rounded-full hover:bg-[var(--primary-tint)]" href={`mailto:${p.email}`} title={p.email}><Mail size={15} /></a>}
                    <button type="button" className="p-1.5 rounded-full hover:bg-[var(--primary-tint)]" onClick={() => onOuvrir(p.creneaux[0])} title="Ouvrir" data-testid="personne-ouvrir"><ChevronRight size={15} /></button>
                  </span>
                </td>
              </tr>
            );
          })}
          {!lignes.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-[var(--muted)]">{q ? "Personne ne correspond." : "Aucun bénévole pour l'instant."}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ── Le panneau latéral ────────────────────────────────────────────────────────────── */

function Tiroir({ onFermer, children }) {
  useEffect(() => {
    const echap = (e) => { if (e.key === "Escape") onFermer(); };
    document.addEventListener("keydown", echap);
    const avant = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", echap); document.body.style.overflow = avant; };
  }, [onFermer]);
  return (
    <div className="fixed inset-0 z-50 flex justify-end print:hidden">
      <div className="absolute inset-0" style={{ background: "rgba(15,27,45,0.32)" }} onClick={onFermer} aria-hidden />
      <div role="dialog" aria-modal="true" className="relative h-full w-full sm:w-[460px] max-w-full bg-white flex flex-col"
        style={{ boxShadow: "-24px 0 48px -12px rgba(15,27,45,0.25)" }} data-testid="benevoles-panneau">
        {children}
      </div>
    </div>
  );
}

const EntetePanneau = ({ icone, titre, sous, onFermer, onRetour }) => (
  <div className="px-5 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "var(--border)" }}>
    {onRetour && <button type="button" onClick={onRetour} className="p-1.5 -ml-1.5 rounded-full hover:bg-[var(--primary-tint)]" title="Retour" data-testid="panneau-retour"><ArrowLeft size={18} /></button>}
    {icone && <span className="text-2xl leading-none mt-0.5" aria-hidden>{icone}</span>}
    <div className="flex-1 min-w-0">
      <div className="font-display text-lg tracking-tight leading-tight">{titre}</div>
      {sous && <div className="text-sm text-[var(--muted)] mt-0.5">{sous}</div>}
    </div>
    <button type="button" onClick={onFermer} className="p-1.5 -mr-1.5 rounded-full hover:bg-[var(--primary-tint)]" title="Fermer" data-testid="panneau-fermer"><X size={18} /></button>
  </div>
);

const BoutonGeste = ({ Icon, children, href, onClick, testid, ton }) => {
  const style = ton === "primaire" ? { background: "var(--primary)", color: "#04202A", borderColor: "var(--primary)" }
    : ton === "danger" ? { color: "#B91C1C", borderColor: "#FECACA" } : { borderColor: "var(--border)" };
  const classe = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-[var(--primary-tint)] transition-colors";
  if (href) return <a className={classe} style={style} href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} data-testid={testid}><Icon size={13} /> {children}</a>;
  return <button type="button" className={classe} style={style} onClick={onClick} data-testid={testid}><Icon size={13} /> {children}</button>;
};

const Contact = ({ p }) => {
  const wa = numeroWhatsApp(p.telephone);
  return (
    <>
      {p.telephone && <BoutonGeste Icon={Phone} href={`tel:${p.telephone}`} testid="geste-appeler">Appeler</BoutonGeste>}
      {wa && <BoutonGeste Icon={MessageCircle} href={`https://wa.me/${wa}`} testid="geste-whatsapp">WhatsApp</BoutonGeste>}
      {p.email && <BoutonGeste Icon={Mail} href={`mailto:${p.email}`} testid="geste-email">E-mail</BoutonGeste>}
    </>
  );
};

function PanneauCase({ x, role, gens, focus, reps, personneDe, actions, onFermer }) {
  const { pris, manque, etat } = comptes(role, gens);
  const t = TEINTES[etat];
  const refFocus = useRef(null);
  useEffect(() => { if (refFocus.current) refFocus.current.scrollIntoView({ block: "nearest" }); }, [focus]);
  return (
    <>
      <EntetePanneau icone={EMOJI[role.role]} titre={role.libelle} sous={`${x.titre} · ${majuscule(sansAnnee(x.quand))}`} onFermer={onFermer} />
      <div className="px-5 py-3 flex items-center gap-2 text-sm border-b" style={{ borderColor: "var(--border-soft)", background: t.fond }}>
        <Pastilles pris={pris} places={role.places} couleur={t.point} />
        <strong style={{ color: t.texte }}>{pris} sur {role.places}</strong>
        <span style={{ color: t.texte }}>{manque ? `· il en manque ${manque}` : pris > role.places ? `· ${pris - role.places} en plus` : "· complet"}</span>
      </div>
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-24 space-y-3">
        {role.description && <p className="text-xs text-[var(--muted)]">{role.description}</p>}
        {gens.map((c) => (
          <div key={c.id} ref={c.id === focus ? refFocus : null}>
            <FicheBenevole c={c} p={personneDe(c)} reps={reps} actions={actions} enAvant={c.id === focus} />
          </div>
        ))}
        {manque > 0 && (
          <div className="rounded-xl border border-dashed px-4 py-3 text-sm" style={{ borderColor: TEINTES.vide.bord, color: TEINTES.vide.texte, background: TEINTES.vide.fond }}>
            {manque > 1 ? `${manque} places à trouver.` : "1 place à trouver."} <span className="text-[var(--muted)]">Pour y mettre quelqu'un d'un autre poste : « Modifier » sur sa fiche.</span>
          </div>
        )}
      </div>
    </>
  );
}

// Un bénévole dans le panneau d'une case : qui, comment le joindre, ses autres créneaux, et les gestes.
function FicheBenevole({ c, p, reps, actions, enAvant }) {
  const par = parQui(c);
  const autres = p.creneaux.filter((x) => x.id !== c.id);
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: enAvant ? "var(--primary)" : "var(--border)", boxShadow: enAvant ? "0 0 0 1px var(--primary)" : "none" }} data-testid={`fiche-${c.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold">{c.prenom} {c.nom}</div>
          <div className="text-[13px] mt-0.5">{c.telephone && <a href={`tel:${c.telephone}`} className="hover:underline">{c.telephone}</a>}</div>
          {c.email && <div className="text-[13px] text-[var(--muted)] break-all">{c.email}</div>}
        </div>
        {c.statut === "inscrit" && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>À confirmer</span>}
      </div>
      <dl className="mt-2.5 space-y-1 text-xs text-[var(--muted)]">
        {par && <div>Inscrit·e par <span className="text-[var(--text)]">{par.nom}</span>{par.email === String(c.email || "").toLowerCase() ? " (reçoit ses e-mails)" : ""}</div>}
        {texteBillet(c, reps) && <div>{texteBillet(c, reps)}</div>}
        {autres.length > 0 && <div>Aussi : {autres.map((x) => `${COURT[x.role]} · ${dateCourte(x.rep)}`).join(", ")}</div>}
        {c.cree_le && <div>Inscrit·e le {inscritLe(c.cree_le)}</div>}
      </dl>
      {c.remarque && <div className="mt-2 text-[13px] italic rounded-lg px-3 py-2" style={{ background: "#F8FAFC" }}>« {c.remarque} »</div>}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Contact p={c} />
        {c.statut === "inscrit" && <BoutonGeste Icon={Check} ton="primaire" onClick={() => actions.confirmer(c)} testid="benevole-confirmer">Confirmer</BoutonGeste>}
        <BoutonGeste Icon={Pencil} onClick={() => actions.modifier(c)} testid="benevole-modifier">Modifier</BoutonGeste>
        <BoutonGeste Icon={X} ton="danger" onClick={() => actions.retirer(c)} testid="benevole-retirer">Retirer</BoutonGeste>
      </div>
    </div>
  );
}

function PanneauPersonne({ p, reps, actions, onFermer }) {
  const par = parQui(p);
  return (
    <>
      <EntetePanneau titre={`${p.prenom} ${p.nom}`} sous={pluriel(p.creneaux.length, "créneau", "créneaux")} onFermer={onFermer} />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-24 space-y-4">
        <div className="space-y-1 text-sm">
          {p.telephone && <div><a href={`tel:${p.telephone}`} className="font-medium hover:underline">{p.telephone}</a></div>}
          {p.email && <div className="text-[var(--muted)] break-all">{p.email}</div>}
          {par && <div className="text-xs text-[var(--muted)]">Inscrit·e par <span className="text-[var(--text)]">{par.nom}</span></div>}
          <div className="flex flex-wrap gap-1.5 pt-2"><Contact p={p} /></div>
        </div>
        <div className="space-y-2" data-testid="personne-creneaux">
          {p.creneaux.map((c) => (
            <div key={c.id} className="rounded-2xl border p-4" style={{ borderColor: "var(--border)" }} data-testid={`fiche-${c.id}`}>
              <div className="flex items-start gap-3">
                <span className="text-xl leading-none" aria-hidden>{EMOJI[c.role]}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{c.rep.roles.find((r) => r.role === c.role)?.libelle || COURT[c.role]}</div>
                  <div className="text-[13px] text-[var(--muted)]">{c.rep.titre} · {majuscule(sansAnnee(c.rep.quand))}</div>
                  {texteBillet(c, reps) && <div className="text-xs text-[var(--muted)] mt-1">{texteBillet(c, reps)}</div>}
                  {c.remarque && <div className="text-[13px] italic mt-1.5">« {c.remarque} »</div>}
                </div>
                {c.statut === "inscrit" && <span className="text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#FFFBEB", color: "#B45309", border: "1px solid #FDE68A" }}>À confirmer</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 pl-8">
                {c.statut === "inscrit" && <BoutonGeste Icon={Check} ton="primaire" onClick={() => actions.confirmer(c)} testid="benevole-confirmer">Confirmer</BoutonGeste>}
                <BoutonGeste Icon={Pencil} onClick={() => actions.modifier(c)} testid="benevole-modifier">Modifier</BoutonGeste>
                <BoutonGeste Icon={X} ton="danger" onClick={() => actions.retirer(c)} testid="benevole-retirer">Retirer</BoutonGeste>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ── Modifier (école) ──────────────────────────────────────────────────────────────── */

const Etiquette = ({ children }) => <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--muted)] mb-2">{children}</div>;
const Champ = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-[var(--muted)] mb-1">{label}</span>
    {children}
  </label>
);

function FormulaireModifier({ c, d, personne, majDonnees, onRetour, onFermer, onEnregistre }) {
  const choix = d.representations.filter((x) => x.ouverte || x.id === c.rep.id);
  const [f, setF] = useState({ representation_id: c.rep.id, role: c.role, prenom: c.prenom || "", nom: c.nom || "", telephone: c.telephone || "", email: c.email || "", remarque: c.remarque || "" });
  const [forcer, setForcer] = useState(false);
  const [prevenir, setPrevenir] = useState(true);
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const champ = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setErreur(""); };
  const cible = choix.find((x) => x.id === f.representation_id) || c.rep;
  const repChange = cible.id !== c.rep.id;
  const memeCreneau = !repChange && f.role === c.role;
  // Les places d'un poste à la date choisie, sans compter ce bénévole lui-même.
  const libres = (r) => {
    const ici = !repChange && r.role === c.role;
    const autres = cible.benevoles.filter((b) => b.role === r.role && b.id !== c.id).length;
    return r.places - Math.max(autres, r.places - r.restantes - (ici ? 1 : 0));
  };
  const roleChoisi = cible.roles.find((r) => r.role === f.role) || cible.roles[0];
  const complet = !memeCreneau && libres(roleChoisi) <= 0;
  const conflit = repChange ? personne.creneaux.find((x) => x.id !== c.id && x.rep.id === cible.id) : null;
  const diff = (k) => String(f[k] || "").trim() !== String(c[k] || "").trim();
  const emailChange = f.email.trim().toLowerCase() !== String(c.email || "").toLowerCase();
  const contactChange = ["prenom", "nom", "telephone", "remarque"].some(diff) || emailChange;
  const rien = memeCreneau && !contactChange;
  const peutPrevenir = c.statut === "confirme" && (!memeCreneau || emailChange);
  const par = parQui(c);
  const aLAcheteur = par && par.email === f.email.trim().toLowerCase();
  // Le billet offert suit la date (comme le serveur) : à part, ou seul dans sa commande.
  let billet = "";
  if (repChange && !c.sans_billet && c.billet_representation_id === c.rep.id && c.billet_id) {
    const cmd = d.commandes.find((x) => x.id === c.billet_id);
    billet = (!c.commande_ref || !cmd || Number(cmd.bebe || 0) <= Number(cmd.plein || 0) - 1)
      ? `Son billet offert passe au ${sansAnnee(cible.quand)}.`
      : `Son billet offert reste pour le ${sansAnnee(c.rep.quand)} (un enfant de 0-2 ans de la commande y garde son adulte).`;
  }
  const bloque = rien || envoi || Boolean(conflit) || (complet && !forcer);
  const enregistrer = async () => {
    setErreur(""); setEnvoi(true);
    try {
      const r = await api(`/billetterie/benevoles/${c.id}`, { method: "PUT", body: JSON.stringify({ ...f, forcer: complet && forcer, prevenir: peutPrevenir && prevenir }) });
      majDonnees(r);
      onEnregistre(r.message);
    } catch (e) { setErreur(e.message); setEnvoi(false); }
  };
  return (
    <>
      <EntetePanneau titre={`Modifier ${c.prenom} ${c.nom}`} sous={`Aujourd'hui : ${COURT[c.role]} · ${dateCourte(c.rep)}`} onFermer={onFermer} onRetour={onRetour} />
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6" data-testid="benevole-formulaire">
        <section>
          <Etiquette>Créneau</Etiquette>
          <Champ label="Représentation">
            <select className="input-field" value={f.representation_id} onChange={(e) => { setF({ ...f, representation_id: e.target.value }); setForcer(false); setErreur(""); }} data-testid="modifier-rep">
              {Object.entries(choix.reduce((acc, x) => { (acc[x.titre] = acc[x.titre] || []).push(x); return acc; }, {})).map(([titre, liste]) => (
                <optgroup key={titre} label={titre}>
                  {liste.map((x) => <option key={x.id} value={x.id}>{majuscule(sansAnnee(x.quand))}{x.id === c.rep.id ? " (actuelle)" : ""}</option>)}
                </optgroup>
              ))}
            </select>
          </Champ>
          <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Poste" data-testid="modifier-postes">
            {cible.roles.map((r) => {
              const n = libres(r);
              const actuel = !repChange && r.role === c.role;
              const sel = f.role === r.role;
              return (
                <button key={r.role} type="button" role="radio" aria-checked={sel} onClick={() => { setF({ ...f, role: r.role }); setForcer(false); setErreur(""); }}
                  className="rounded-xl border px-3 py-2 text-left transition-colors" data-testid={`modifier-role-${r.role}`}
                  style={{ borderColor: sel ? "var(--primary)" : "var(--border)", boxShadow: sel ? "0 0 0 1px var(--primary)" : "none", background: sel ? "var(--primary-tint)" : "#fff" }}>
                  <span className="flex items-center gap-2 text-[13px] font-medium"><span aria-hidden>{EMOJI[r.role]}</span>{COURT[r.role]}</span>
                  <span className="block text-[11px] mt-0.5" style={{ color: actuel ? "var(--muted)" : n > 0 ? "#15803D" : TEINTES.vide.texte }}>
                    {actuel ? "son poste actuel" : n > 0 ? `${n} place${n > 1 ? "s" : ""} libre${n > 1 ? "s" : ""}` : "complet"}
                  </span>
                </button>
              );
            })}
          </div>
          {complet && !conflit && (
            <div className="mt-3 rounded-xl px-3.5 py-3 text-[13px]" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }} data-testid="modifier-complet">
              <div className="flex gap-2"><AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <span><strong>{roleChoisi.libelle}</strong> est déjà complet le {sansAnnee(cible.quand)} ({roleChoisi.places} place{roleChoisi.places > 1 ? "s" : ""}). {c.prenom} y sera en plus des places prévues.</span></div>
              <label className="flex items-center gap-2 mt-2.5 pl-6 font-medium cursor-pointer">
                <input type="checkbox" checked={forcer} onChange={(e) => setForcer(e.target.checked)} style={{ accentColor: "#B45309", width: 16, height: 16 }} data-testid="modifier-forcer" />
                Placer {c.prenom} quand même
              </label>
            </div>
          )}
          {conflit && (
            <div className="mt-3 rounded-xl px-3.5 py-3 text-[13px] flex gap-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="modifier-conflit">
              <AlertTriangle size={15} className="shrink-0 mt-0.5" />
              <span>{c.prenom} est déjà bénévole ce jour-là ({COURT[conflit.role]}). Change plutôt ce créneau-là, ou retire-le d'abord.</span>
            </div>
          )}
          {billet && <p className="mt-3 text-[13px] text-[var(--muted)]" data-testid="modifier-billet">🎟 {billet}</p>}
        </section>
        <section>
          <Etiquette>Coordonnées</Etiquette>
          <div className="grid grid-cols-2 gap-3">
            <Champ label="Prénom"><input className="input-field" value={f.prenom} onChange={champ("prenom")} data-testid="modifier-prenom" /></Champ>
            <Champ label="Nom"><input className="input-field" value={f.nom} onChange={champ("nom")} data-testid="modifier-nom" /></Champ>
          </div>
          <div className="mt-3 space-y-3">
            <Champ label="Téléphone"><input className="input-field" type="tel" value={f.telephone} onChange={champ("telephone")} data-testid="modifier-telephone" /></Champ>
            <Champ label={par ? `E-mail (aujourd'hui : ${aLAcheteur ? `celui de ${par.nom}, qui l'a inscrit·e` : "le sien"})` : "E-mail"}>
              <input className="input-field" type="email" value={f.email} onChange={champ("email")} data-testid="modifier-email" /></Champ>
          </div>
          {personne.creneaux.length > 1 && <p className="text-xs text-[var(--muted)] mt-2">Le nom, le téléphone et l'e-mail changent sur ses {personne.creneaux.length} créneaux.</p>}
        </section>
        <section>
          <Etiquette>Remarque</Etiquette>
          <textarea className="input-field" rows={2} value={f.remarque} onChange={champ("remarque")} placeholder="Une précision pour l'école (dispo, matériel…)" data-testid="modifier-remarque" />
        </section>
        {(!memeCreneau || emailChange) && (
          <section className="rounded-xl border px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid="modifier-prevenir-bloc">
            {peutPrevenir ? (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={prevenir} onChange={(e) => setPrevenir(e.target.checked)} className="mt-0.5" style={{ accentColor: "var(--primary)", width: 16, height: 16 }} data-testid="modifier-prevenir" />
                <span className="text-sm">
                  <span className="font-medium">Prévenir {aLAcheteur ? par.nom : f.prenom || c.prenom} par e-mail</span>
                  <span className="block text-xs text-[var(--muted)] mt-0.5">
                    {!prevenir
                      ? `Aucun e-mail ne part : préviens ${c.prenom} toi-même${billet.includes("passe") ? ", et renvoie ses billets (Billets & commandes › Renvoyer)" : ""}.`
                      : !memeCreneau
                        ? `« ${aLAcheteur ? `Le créneau de ${c.prenom} a changé` : "Ton créneau a changé"} », avec le groupe WhatsApp et son lien personnel${billet.includes("passe") ? ", et ses billets à jour" : ""}.`
                        : "La confirmation repart à la nouvelle adresse : groupe WhatsApp, lien personnel et billet offert."}
                  </span>
                </span>
              </label>
            ) : (
              <p className="text-xs text-[var(--muted)]">Pas encore confirmé : aucun e-mail ne part maintenant. « Confirmer » lui enverra la confirmation, avec son créneau à jour.</p>
            )}
          </section>
        )}
        {erreur && <div className="rounded-xl px-3.5 py-3 text-[13px]" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="modifier-erreur">{erreur}</div>}
      </div>
      {/* Les boutons à gauche : le bouton « Aide » de la console flotte en bas à droite. */}
      <div className="px-5 py-4 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
        <button type="button" className="btn-primary text-sm disabled:opacity-50" disabled={bloque} onClick={enregistrer} data-testid="modifier-enregistrer">
          {envoi ? "Enregistrement…" : complet && forcer ? "Placer en plus" : "Enregistrer"}
        </button>
        <button type="button" className="btn-ghost text-sm" onClick={onRetour || onFermer}>Annuler</button>
      </div>
    </>
  );
}

/* ── Le message en bas de l'écran ──────────────────────────────────────────────────── */

const Bulle = ({ bulle, onFermer }) => (
  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[min(560px,calc(100vw-32px))] print:hidden" role="status" aria-live="polite">
    <div className="rounded-2xl px-4 py-3 text-sm flex items-start gap-3"
      style={{ background: bulle.ton === "ok" ? "#0F1B2D" : "#FEF2F2", color: bulle.ton === "ok" ? "#fff" : "#B91C1C", border: bulle.ton === "ok" ? "none" : "1px solid #FECACA", boxShadow: "0 16px 40px -12px rgba(15,27,45,0.45)" }}
      data-testid="benevoles-bulle">
      {bulle.ton === "ok" ? <Check size={16} className="shrink-0 mt-0.5" style={{ color: "#4ADE80" }} /> : <AlertTriangle size={16} className="shrink-0 mt-0.5" />}
      <span className="flex-1">{bulle.texte}</span>
      <button type="button" onClick={onFermer} className="opacity-70 hover:opacity-100" title="Fermer"><X size={15} /></button>
    </div>
  </div>
);

/* ── Impression (A4 paysage) ───────────────────────────────────────────────────────── */

function Impression({ mode, reps, roles, creneaux, personnes, rep, manque, places }) {
  const titre = { tableau: "Tableau de service", dates: "Liste par date", personnes: "Liste des bénévoles" }[mode];
  const le = new Date().toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" });
  const entete = (sous) => (
    <div className="feuille-entete flex items-baseline justify-between gap-4 mb-3 pb-2" style={{ borderBottom: "1.5px solid #0F1B2D" }}>
      <div><div className="text-[17pt] font-bold leading-tight">Bénévoles des spectacles · {titre}</div>{sous && <div className="text-[11pt]">{sous}</div>}</div>
      <div className="text-[9pt] text-right" style={{ color: "#475569" }}>1000 ARTS · imprimé le {le}<br />{manque ? `Il manque ${manque} bénévole${manque > 1 ? "s" : ""} sur ${places}` : "Tous les postes sont pris"}</div>
    </div>
  );
  if (mode === "tableau") {
    return <div className="feuille">{entete("")}<div className="text-[9pt]"><TableauService reps={reps} roles={roles} impression /></div></div>;
  }
  if (mode === "personnes") {
    const liste = personnes.map((p) => ({ ...p, creneaux: p.creneaux.filter((c) => !rep || c.rep.id === rep) })).filter((p) => p.creneaux.length);
    return (
      <div className="feuille">
        {entete(rep ? `${reps[0]?.titre} · ${majuscule(sansAnnee(reps[0]?.quand))}` : `${liste.length} personnes`)}
        <table className="w-full text-[9.5pt]" style={{ borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #94A3B8" }}>{["Bénévole", "Téléphone", "E-mail", "Créneaux"].map((h) => <th key={h} className="text-left py-1 pr-3">{h}</th>)}</tr></thead>
          <tbody>
            {liste.map((p) => (
              <tr key={p.cle} style={{ borderBottom: "1px solid #E2E8F0" }}>
                <td className="py-1 pr-3 font-semibold whitespace-nowrap">{p.prenom} {p.nom}</td>
                <td className="py-1 pr-3 whitespace-nowrap">{p.telephone}</td>
                <td className="py-1 pr-3">{p.email}</td>
                <td className="py-1">{p.creneaux.map((c) => `${COURT[c.role]} · ${dateCourte(c.rep)}`).join(" ; ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // Une page par date : chaque poste, ses bénévoles et une ligne vide par place à trouver.
  return reps.filter((x) => x.ouverte || x.benevoles.length).map((x) => {
    const ici = creneaux.filter((c) => c.rep.id === x.id);
    return (
      <div key={x.id} className="feuille">
        {entete(`${x.titre} · ${majuscule(sansAnnee(x.quand))} · portes ${x.portes}`)}
        <table className="w-full text-[9.5pt]" style={{ borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #94A3B8" }}>{["Poste", "Bénévole", "Téléphone", "Remarque", "Là"].map((h) => <th key={h} className="text-left py-1 pr-3">{h}</th>)}</tr></thead>
          <tbody>
            {x.roles.flatMap((role) => {
              const gens = ici.filter((c) => c.role === role.role);
              const vides = Math.max(0, role.places - gens.length);
              const lignes = [...gens, ...Array.from({ length: vides }, () => null)];
              return lignes.map((c, i) => (
                <tr key={`${role.role}-${c ? c.id : `v${i}`}`} style={{ borderBottom: i === lignes.length - 1 ? "1px solid #94A3B8" : "1px solid #E2E8F0" }}>
                  <td className="py-[3px] pr-3 whitespace-nowrap font-semibold">{i === 0 ? `${EMOJI[role.role]} ${role.libelle} (${gens.length}/${role.places})` : ""}</td>
                  <td className="py-[3px] pr-3">{c ? `${c.prenom} ${c.nom}` : <span style={{ color: "#BE123C" }}>à trouver</span>}</td>
                  <td className="py-[3px] pr-3 whitespace-nowrap">{c?.telephone || ""}</td>
                  <td className="py-[3px] pr-3 italic">{c?.remarque || ""}</td>
                  <td className="py-[3px] w-8"><span className="inline-block w-3 h-3 border" style={{ borderColor: "#64748B" }} /></td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    );
  });
}
